<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview
        :title="isEdit ? 'Sửa yêu cầu bổ sung ngân sách' : 'Tạo yêu cầu bổ sung ngân sách'"
      >
        <template v-slot:preview>
          <b-form @submit.prevent="onSaveDraft">
            <b-row class="mb-3">
              <!-- Kỳ (Tháng/Năm) -->
              <b-col cols="12" md="3">
                <b-form-group>
                  <template #label>
                    Kỳ (Tháng/Năm) <span class="text-danger">*</span>
                  </template>
                  <date-picker
                    v-model="periodDisplay"
                    :config="dpMonth"
                    class="form-control-sm"
                    placeholder="MM/YYYY"
                    @dp-change="onPeriodChange"
                  />
                </b-form-group>
              </b-col>

              <!-- Doanh nghiệp -->
              <b-col cols="12" md="3">
                <b-form-group>
                  <template #label>
                    Doanh nghiệp <span class="text-danger">*</span>
                  </template>
                  <b-form-select
                    v-model="form.companyId"
                    :options="companyOptions"
                    size="sm"
                    @change="fetchCurrentBudget"
                  />
                </b-form-group>
              </b-col>

              <!-- Phòng ban -->
              <b-col cols="12" md="3">
                <b-form-group>
                  <template #label>
                    Phòng ban <span class="text-danger">*</span>
                  </template>
                  <Autosuggest
                    :model="departmentModel"
                    :suggestions="departmentSuggestions"
                    suggestionName="name"
                    placeholder="phòng ban"
                    :limit="50"
                    @select="onSelectedDepartment"
                    @change="onInputDepartment"
                  >
                    <template #custom="{ suggestion }">{{
                      suggestion.item.name
                    }}</template>
                  </Autosuggest>
                </b-form-group>
              </b-col>

              <!-- Cửa hàng -->
              <b-col cols="12" md="3">
                <b-form-group label="Cửa hàng">
                  <Autosuggest
                    :model="storeModel"
                    :suggestions="storeSuggestions"
                    suggestionName="name"
                    placeholder="cửa hàng"
                    :limit="50"
                    @select="onSelectedStore"
                    @change="onInputStore"
                  >
                    <template #custom="{ suggestion }">{{
                      suggestion.item.name
                    }}</template>
                  </Autosuggest>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <!-- Khoản mục phí -->
              <b-col cols="12" md="3">
                <b-form-group>
                  <template #label>
                    Khoản mục phí <span class="text-danger">*</span>
                  </template>
                  <Autosuggest
                    :model="costItemModel"
                    :suggestions="costItemSuggestions"
                    suggestionName="name"
                    placeholder="khoản mục phí"
                    :limit="50"
                    @select="onSelectedCostItem"
                    @change="onInputCostItem"
                  >
                    <template #custom="{ suggestion }">{{
                      suggestion.item.name
                    }}</template>
                  </Autosuggest>
                </b-form-group>
              </b-col>

              <!-- Số tiền yêu cầu bổ sung -->
              <b-col cols="12" md="4">
                <b-form-group>
                  <template #label>
                    Số tiền yêu cầu bổ sung <span class="text-danger">*</span>
                  </template>
                  <BInputNumber
                    v-model="form.requestedAmount"
                    placeholder="0"
                    size="sm"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Info block ngân sách hiện tại -->
            <b-row class="mb-3">
              <b-col cols="12" md="8">
                <b-alert variant="info" :show="currentBudget.found === true">
                  Ngân sách tháng này:
                  <strong
                    >{{ convertPrice(currentBudget.allocatedAmount) }} đ</strong
                  >
                  | Đã dùng:
                  <strong
                    >{{ convertPrice(currentBudget.usedAmount) }} đ</strong
                  >
                  | Còn lại:
                  <strong
                    >{{ convertPrice(currentBudget.remainingAmount) }} đ</strong
                  >
                </b-alert>
                <b-alert
                  variant="warning"
                  :show="currentBudget.found === false"
                >
                  Chưa có ngân sách tháng này. Khi duyệt sẽ tạo mới ngân sách.
                </b-alert>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <!-- Lý do yêu cầu -->
              <b-col cols="12" md="8">
                <b-form-group>
                  <template #label>
                    Lý do yêu cầu <span class="text-danger">*</span>
                  </template>
                  <b-form-textarea
                    v-model="form.reason"
                    rows="3"
                    placeholder="Nhập lý do yêu cầu bổ sung ngân sách"
                    maxlength="1000"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Người duyệt -->
            <b-row class="mb-4">
              <b-col cols="12" md="8">
                <ApprovalStepSearchSelector
                  :steps="approvalSteps"
                  emptyText="Chưa có cấu hình duyệt cho loại phiếu này."
                  @change="approvalSteps = $event"
                />
              </b-col>
            </b-row>

            <!-- Buttons -->
            <b-row>
              <b-col>
                <b-button
                  variant="primary"
                  :disabled="isSaving"
                  @click="onSaveDraft"
                >
                  <i class="fas fa-save"></i>
                  {{ isSaving ? 'Đang lưu...' : 'Lưu nháp' }}
                </b-button>
                <b-button
                  v-if="!isEdit"
                  variant="success"
                  class="ml-2"
                  :disabled="isSaving"
                  @click="onSaveAndSubmit"
                >
                  <i class="fas fa-paper-plane"></i>
                  {{ isSaving ? 'Đang xử lý...' : 'Lưu & gửi duyệt' }}
                </b-button>
                <b-button variant="secondary" class="ml-2" @click="goBack">
                  <i class="fas fa-arrow-left"></i> Quay lại
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { checkPermission, getData } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  handleError,
  xoa_dau,
} from '@/utils/common';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import ApprovalStepSearchSelector from '@/view/components/approval/ApprovalStepSearchSelector.vue';
import moment from 'moment';
import { APPROVAL_CONFIG_TYPE } from '@/utils/enum';
import { mergeApprovalConfigDetailsByStep } from '@/utils/approval-config';

export default {
  components: {
    KTCodePreview,
    BInputNumber,
    Autosuggest,
    ApprovalStepSearchSelector,
  },
  data() {
    return {
      isEdit: false,
      isSaving: false,
      periodDisplay: '',
      departmentModel: '',
      storeModel: '',
      costItemModel: '',
      departmentSource: [],
      departmentSuggestions: [],
      storeSource: [],
      storeSuggestions: [],
      costItemSource: [],
      costItemSuggestions: [],
      form: {
        departmentId: null,
        costItemId: null,
        storeId: null,
        companyId: null,
        period: '',
        requestedAmount: 0,
        reason: '',
      },
      approvalSteps: [],
      suggestLimit: 50,
      companyOptions: [{ value: null, text: '-- Doanh nghiệp --' }],
      currentBudget: {
        found: null,
        allocatedAmount: 0,
        usedAmount: 0,
        remainingAmount: 0,
      },
      dpMonth: {
        format: 'MM/YYYY',
        useCurrent: false,
        showTodayButton: false,
        locale: 'vi',
        minDate: false,
      },
    };
  },
  created() {
    const user = getData('user');
    this.form.companyId = (user && user.companyId) || null;
    this.fetchCompanies();
    this.fetchDepartments();
    this.fetchStores();
    this.fetchCostItems();
    this.fetchApprovalConfig();

    const q = this.$route.query;
    const id = q.id;
    if (id) {
      this.isEdit = true;
      this.fetchDetail(id);
    } else {
      // Pre-fill from query params (e.g. coming from budget-line detail)
      if (q.period) {
        this.periodDisplay = moment(q.period).format('MM/YYYY');
        this.form.period = moment(q.period)
          .startOf('month')
          .format('YYYY-MM-DD');
      }
      if (q.departmentId) this.form.departmentId = Number(q.departmentId);
      if (q.costItemId) this.form.costItemId = Number(q.costItemId);
      if (q.storeId) this.form.storeId = Number(q.storeId);
      if (q.companyId) this.form.companyId = Number(q.companyId);
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Yêu cầu bổ sung NS', route: '/budget/budget-line-requests' },
      { title: this.isEdit ? 'Sửa' : 'Tạo mới' },
    ]);
  },
  methods: {
    checkPermission,
    makeToastSuccess,
    makeToastFaile,
    convertPrice,

    onPeriodChange(e) {
      const val = e && e.date ? e.date : null;
      if (val) {
        this.form.period = moment(val).startOf('month').format('YYYY-MM-DD');
      } else {
        this.form.period = '';
      }
      this.fetchCurrentBudget();
    },

    fetchCompanies() {
      ApiService.get('/company')
        .then(({ data }) => {
          const rows = data.data || [];
          this.companyOptions = [
            { value: null, text: '-- Doanh nghiệp --' },
            ...rows.map((c) => ({ value: c.id, text: c.name })),
          ];
        })
        .catch(() => {});
    },
    filterSuggestionsByName(source, text, limit) {
      const list = Array.isArray(source) ? source : [];
      const t = text != null ? String(text) : '';
      if (!t.trim()) return list.slice(0, limit);
      const tLower = t.toLowerCase();
      return list
        .filter((item) => {
          const name = item && item.name != null ? String(item.name) : '';
          return (
            xoa_dau(name).toLowerCase().indexOf(tLower) > -1 ||
            name.toLowerCase().indexOf(tLower) > -1
          );
        })
        .slice(0, limit);
    },
    filterStoreSuggestions(source, text, limit) {
      const list = Array.isArray(source) ? source : [];
      const t = text != null ? String(text) : '';
      if (!t.trim()) return list.slice(0, limit);
      const tLower = t.toLowerCase();
      return list
        .filter((item) => {
          const name = item && item.name != null ? String(item.name) : '';
          const shortName =
            item && item.shortName != null ? String(item.shortName) : '';
          return [name, shortName].filter(Boolean).some((f) => {
            return (
              xoa_dau(f).toLowerCase().indexOf(tLower) > -1 ||
              f.toLowerCase().indexOf(tLower) > -1
            );
          });
        })
        .slice(0, limit);
    },
    syncDepartmentSuggestionsFromModel() {
      this.departmentSuggestions = this.filterSuggestionsByName(
        this.departmentSource,
        this.departmentModel,
        this.suggestLimit,
      );
    },
    syncCostItemSuggestionsFromModel() {
      this.costItemSuggestions = this.filterSuggestionsByName(
        this.costItemSource,
        this.costItemModel,
        this.suggestLimit,
      );
    },
    syncStoreSuggestionsFromModel() {
      this.storeSuggestions = this.filterStoreSuggestions(
        this.storeSource,
        this.storeModel,
        this.suggestLimit,
      );
    },
    fetchDepartments() {
      ApiService.query('departments/all')
        .then(({ data }) => {
          const rows = data.data || [];
          this.departmentSource = rows.map((d) => ({
            id: Number(d.id),
            name: d.name,
          }));
          if (this.form.departmentId && !this.departmentModel) {
            const found = this.departmentSource.find(
              (d) => d.id === Number(this.form.departmentId),
            );
            if (found) this.departmentModel = found.name;
          }
          this.syncDepartmentSuggestionsFromModel();
        })
        .catch(() => {});
    },
    fetchStores() {
      ApiService.query('stores/getStores')
        .then(({ data }) => {
          const rows = data.data || [];
          this.storeSource = rows.map((s) => ({
            id: Number(s.id),
            name: s.name,
            shortName: s.shortName || '',
          }));
          if (this.form.storeId && !this.storeModel) {
            const found = this.storeSource.find(
              (s) => s.id === Number(this.form.storeId),
            );
            if (found)
              this.storeModel = found.shortName
                ? `${found.name} (${found.shortName})`
                : found.name;
          }
          this.syncStoreSuggestionsFromModel();
        })
        .catch(() => {});
    },
    fetchCostItems() {
      ApiService.get('v2/cost-items/all')
        .then(({ data }) => {
          const rows = data.data || [];
          this.costItemSource = rows.map((c) => ({
            id: Number(c.id),
            name: c.name,
          }));
          if (this.form.costItemId && !this.costItemModel) {
            const found = this.costItemSource.find(
              (c) => c.id === Number(this.form.costItemId),
            );
            if (found) this.costItemModel = found.name;
          }
          this.syncCostItemSuggestionsFromModel();
        })
        .catch(() => {});
    },
    mapApprovalSteps(details, selectedApprovers = []) {
      const selectedByStep = (selectedApprovers || []).reduce((acc, item) => {
        acc[item.step] = item.approverBy;
        return acc;
      }, {});
      const merged = mergeApprovalConfigDetailsByStep(details || []);
      this.approvalSteps = merged.map((detail) => ({
        step: detail.step,
        approverBy: selectedByStep[detail.step] || null,
        owners: (detail.owners || [])
          .filter((o) => o && o.id)
          .filter(
            (o, idx, arr) =>
              arr.findIndex((x) => Number(x.id) === Number(o.id)) === idx,
          )
          .map((o) => ({
            id: Number(o.id),
            name: o.name || o.fullName || String(o.id),
            code: o.code || '',
          })),
      }));
    },
    fetchApprovalConfig(selectedApprovers = []) {
      ApiService.get(
        `v2/approval-configs/${APPROVAL_CONFIG_TYPE.BUDGET_REQUEST}/approvers`,
      )
        .then(({ data }) => {
          if (data.status === 200 && data.data) {
            this.mapApprovalSteps(data.data.details || [], selectedApprovers);
          } else {
            this.approvalSteps = [];
          }
        })
        .catch(() => {
          this.approvalSteps = [];
        });
    },
    fetchDetail(id) {
      ApiService.get(`v2/budget-line-requests/${id}`)
        .then(({ data }) => {
          const req = data.data || {};
          this.form.departmentId = req.departmentId || null;
          this.form.costItemId = req.costItemId || null;
          this.form.storeId = req.storeId || null;
          this.form.companyId = req.companyId || this.form.companyId;
          this.form.period = req.period || '';
          this.form.requestedAmount = req.requestedAmount || 0;
          this.form.reason = req.reason || '';

          if (req.period) {
            this.periodDisplay = moment(req.period).format('MM/YYYY');
          }
          if (req.departmentName) this.departmentModel = req.departmentName;
          if (req.storeName) this.storeModel = req.storeName;
          if (req.costItemName) this.costItemModel = req.costItemName;

          this.fetchApprovalConfig(req.approvers || []);

          this.$nextTick(() => {
            this.syncDepartmentSuggestionsFromModel();
            this.syncCostItemSuggestionsFromModel();
            this.syncStoreSuggestionsFromModel();
          });
        })
        .catch((err) => handleError(err));
    },
    fetchCurrentBudget() {
      const { period, departmentId, costItemId, companyId } = this.form;
      if (!period || !departmentId || !costItemId || !companyId) {
        this.$set(this.currentBudget, 'found', null);
        return;
      }
      const params = {
        period,
        departmentId,
        costItemId,
        companyId,
      };
      if (this.form.storeId) params.storeId = this.form.storeId;
      ApiService.query('v2/budget-line-requests/current-budget', { params })
        .then(({ data }) => {
          const d = data.data;
          if (d && d.id) {
            this.$set(this.currentBudget, 'found', true);
            this.$set(
              this.currentBudget,
              'allocatedAmount',
              d.allocatedAmount || 0,
            );
            this.$set(this.currentBudget, 'usedAmount', d.usedAmount || 0);
            this.$set(
              this.currentBudget,
              'remainingAmount',
              d.remainingAmount || 0,
            );
          } else {
            this.$set(this.currentBudget, 'found', false);
          }
        })
        .catch(() => {
          this.$set(this.currentBudget, 'found', false);
        });
    },

    onInputDepartment(text) {
      this.departmentModel = (text || '').toString();
      if (!this.departmentModel.trim()) this.form.departmentId = null;
      this.syncDepartmentSuggestionsFromModel();
    },
    onSelectedDepartment(option) {
      const item = option && option.item ? option.item : null;
      this.form.departmentId = item ? Number(item.id) : null;
      this.departmentModel = item ? item.name : '';
      this.departmentSuggestions = this.departmentSource.slice(
        0,
        this.suggestLimit,
      );
      this.fetchCurrentBudget();
    },

    onInputStore(text) {
      this.storeModel = (text || '').toString();
      if (!this.storeModel.trim()) this.form.storeId = null;
      this.syncStoreSuggestionsFromModel();
    },
    onSelectedStore(option) {
      const item = option && option.item ? option.item : null;
      this.form.storeId = item ? Number(item.id) : null;
      this.storeModel = item
        ? item.shortName
          ? `${item.name} (${item.shortName})`
          : item.name
        : '';
      this.storeSuggestions = this.storeSource.slice(0, this.suggestLimit);
      this.fetchCurrentBudget();
    },

    onInputCostItem(text) {
      this.costItemModel = (text || '').toString();
      if (!this.costItemModel.trim()) this.form.costItemId = null;
      this.syncCostItemSuggestionsFromModel();
    },
    onSelectedCostItem(option) {
      const item = option && option.item ? option.item : null;
      this.form.costItemId = item ? Number(item.id) : null;
      this.costItemModel = item ? item.name : '';
      this.costItemSuggestions = this.costItemSource.slice(
        0,
        this.suggestLimit,
      );
      this.fetchCurrentBudget();
    },

    validate() {
      if (!this.form.companyId) {
        this.makeToastFaile('Vui lòng chọn doanh nghiệp!');
        return false;
      }
      if (!this.form.period) {
        this.makeToastFaile('Vui lòng chọn kỳ (tháng/năm)!');
        return false;
      }
      if (!this.form.departmentId) {
        this.makeToastFaile('Vui lòng chọn phòng ban!');
        return false;
      }
      if (!this.form.costItemId) {
        this.makeToastFaile('Vui lòng chọn khoản mục phí!');
        return false;
      }
      if (
        !this.form.requestedAmount ||
        Number(this.form.requestedAmount) <= 0
      ) {
        this.makeToastFaile('Số tiền yêu cầu phải lớn hơn 0!');
        return false;
      }
      if (!this.form.reason || !this.form.reason.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do yêu cầu!');
        return false;
      }
      const invalidStep = this.approvalSteps.find((s) => !s.approverBy);
      if (invalidStep) {
        this.makeToastFaile(
          `Vui lòng chọn người duyệt cho bước ${invalidStep.step}!`,
        );
        return false;
      }
      return true;
    },

    buildPayload() {
      return {
        departmentId: this.form.departmentId,
        costItemId: this.form.costItemId,
        storeId: this.form.storeId || undefined,
        companyId: this.form.companyId,
        period: this.form.period,
        requestedAmount: Number(this.form.requestedAmount),
        reason: this.form.reason,
        approvers: this.approvalSteps.map((s) => ({
          step: s.step,
          approverBy: s.approverBy,
        })),
      };
    },

    onSaveDraft() {
      if (!this.validate()) return;
      this.isSaving = true;
      const promise = this.isEdit
        ? ApiService.put(
            `v2/budget-line-requests/${this.$route.query.id}`,
            this.buildPayload(),
          )
        : ApiService.post('v2/budget-line-requests/', this.buildPayload());

      promise
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Lưu thành công');
          if (this.isEdit) {
            const id = (data.data && data.data.id) || this.$route.query.id;
            this.$router.push({
              name: 'detail-budget-line-request',
              query: { id },
            });
          } else {
            this.$router.push({
              name: 'list-budget-line-requests',
              query: { sortBy: 'createdAt' },
            });
          }
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.isSaving = false;
        });
    },

    onSaveAndSubmit() {
      if (!this.validate()) return;
      this.isSaving = true;
      ApiService.post('v2/budget-line-requests/', this.buildPayload())
        .then(({ data }) => {
          const id = data.data && data.data.id;
          if (!id) {
            this.makeToastSuccess(data.message || 'Lưu thành công');
            this.$router.push({ name: 'list-budget-line-requests', query: { sortBy: 'createdAt' } });
            return;
          }
          return ApiService.post(
            `v2/budget-line-requests/${id}/submit`,
            {},
          ).then(({ data: submitData }) => {
            this.makeToastSuccess(submitData.message || 'Đã submit thành công');
            this.$router.push({
              name: 'detail-budget-line-request',
              query: { id },
            });
          });
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.isSaving = false;
        });
    },

    goBack() {
      const from = this.$route.query.from;
      if (from) {
        this.$router.push(String(from)).catch(() => {});
        return;
      }
      this.$router.push({ name: 'list-budget-line-requests' }).catch(() => {});
    },
  },
};
</script>

<style scoped>
</style>
